import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAdminById } from '@/services/ant-design-pro/api';
import {
  AUTH_STORAGE_KEY,
  authPersistStorageAdapter,
  clearAllAuthStorage,
  setAuthPersistenceMode,
  setAuthToken,
  setKeepLoggedInPreference,
} from '@/utils/authPersistence';

export interface AdminUser {
  _id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  lastLogin?: string;
}

export interface AuthState {
  token: string | null;
  user: AdminUser | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: AdminUser, keepLoggedIn?: boolean) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<AdminUser>) => void;
  refreshPermissions: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      setAuth: (token: string, user: AdminUser, keepLoggedIn = true) => {
        clearAllAuthStorage();
        setAuthPersistenceMode(keepLoggedIn ? 'local' : 'session');
        setKeepLoggedInPreference(keepLoggedIn);

        set({
          token,
          user,
          isAuthenticated: true,
        });

        setAuthToken(token, keepLoggedIn);
      },
      clearAuth: () => {
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        });
        clearAllAuthStorage();
        try {
          const { clearStatisticsCache } = require('@/utils/memberStatisticsCache');
          clearStatisticsCache();
        } catch (error) {
          // console.error('Error clearing statistics cache:', error);
        }
      },
      updateUser: (user: Partial<AdminUser>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...user } : null,
        }));
      },
      refreshPermissions: async () => {
        const state = useAuthStore.getState();
        if (!state.user?._id || !state.token) {
          return;
        }

        try {
          const response = await getAdminById(state.user._id);
          const freshAdmin = response?.data?.admin;

          if (freshAdmin) {
            set((currentState) => ({
              user: currentState.user
                ? {
                    ...currentState.user,
                    permissions: freshAdmin.permissions || [],
                    name: freshAdmin.name || currentState.user.name,
                    email: freshAdmin.email || currentState.user.email,
                    role: freshAdmin.role || currentState.user.role,
                    isActive: freshAdmin.isActive ?? currentState.user.isActive,
                    updatedAt: freshAdmin.updatedAt,
                    lastLogin: freshAdmin.lastLogin || currentState.user.lastLogin,
                  }
                : null,
            }));
          }
        } catch (error) {
          // console.error('❌ Error refreshing permissions:', error);
        }
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: authPersistStorageAdapter,
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
